/*
 * DIE KETTENZEILE UEBER JEDER BUEHNE (Rafael, 03.10.2026 08:15:26Z, Wahl a: „der Guide zeigt
 * die Kette"; Befund BEFUND-310ff7e4-buehnen-neue-buehne-statt-umschreiben-20261003.md).
 *
 * Seit eine beantwortete Buehne eingefroren ist, geht es nach jeder Antwort auf einer neuen
 * Buehne weiter. Damit Rafael dort sieht, woran er gerade anknuepft, steht oben eine feste
 * Zeile: „vorher: <Titel der Vorgaengerin> · deine Antwort: <Frage> → <gewaehlte Option>".
 * Ein Klick oeffnet die Vorgaengerin (als HistorischeBuehne, weil sie abgeschlossen ist).
 * Ohne Vorgaengerin keine Zeile; ohne Schalter (`kette_an`) ebenfalls keine — dann sieht
 * der Guide aus wie vor dem 03.10. Dieselbe Komponente in beiden Haeusern.
 *
 * Bisher stand die Kette nur in Seitendateien (BUEHNEN-KETTE:AUTO, bin/buehnen-kette-nav.py):
 * relative Links, die im abgeschotteten Ursprung tot waren, und dabei teils falsch.
 */
import { useEffect, useState } from 'react';
import { httpBoundary } from '@werkingflow/trust-boundary';
import { buehnenLeseFehlermeldung, buehnenLeseUrl } from '../../shared/schnellbuehnen';
import type { BuehneAnsicht, Entscheidung } from './GuideHof';
import { z } from 'zod';

const KettenBuehne = z.object({
  titel: z.string(),
  entscheidungen: z.array(z.object({
    titel: z.string(),
    antwort: z.object({ art: z.enum(['option', 'freitext']).optional(), kuerzel: z.string().optional(),
      text: z.string(), am: z.string(), von: z.string() }).passthrough().optional(),
  }).passthrough()).default([]),
}).passthrough();

/** Die zuletzt beantwortete Frage einer Buehne — die, an die die Nachfolgerin anknuepft. */
export function letzteAntwort(b: Pick<BuehneAnsicht, 'entscheidungen'>): Entscheidung | null {
  const beantwortet = (b.entscheidungen ?? []).filter(e => !!e.antwort);
  if (!beantwortet.length) return null;
  return beantwortet.reduce((a, e) => ((e.antwort!.am ?? '') > (a.antwort!.am ?? '') ? e : a));
}

/** Die gewaehlte Option in Worten: Kuerzel + Text, bei freier Antwort Rafaels Satz. */
export function antwortWort(e: Entscheidung): string {
  const a = e.antwort;
  if (!a) return '';
  if (a.art === 'freitext') return `„${a.text.length > 120 ? `${a.text.slice(0, 119)}…` : a.text}"`;
  return a.kuerzel ? `${a.kuerzel}) ${a.text}` : a.text;
}

/** Der Text der Zeile, rein abgeleitet — testbar ohne Netz. */
export function kettenZeileText(vorher: Pick<BuehneAnsicht, 'titel' | 'entscheidungen'>): string {
  const e = letzteAntwort(vorher);
  return e
    ? `vorher: ${vorher.titel} · deine Antwort: ${e.titel} → ${antwortWort(e)}`
    : `vorher: ${vorher.titel} · dort keine Antwort`;
}

/** Soll ueber dieser Buehne eine Kettenzeile stehen? */
export function zeigtKettenZeile(b: Pick<BuehneAnsicht, 'kette_an' | 'vorgaenger'>): boolean {
  return !!b.kette_an && (b.vorgaenger?.length ?? 0) > 0;
}

type Geladen = { id: string; buehne: BuehneAnsicht | null; fehler: string | null };

/**
 * IN DER KOPFZEILE DER OFFENEN BUEHNE (`inZeile`, Rafael 03.10.2026 16:05: Guide-Kopf
 * kompakter): die Zeile steht als Brotkrume neben „← Räume" und Erledigt/Später, kuerzt
 * sich mit „…" und zeigt NIE die rohe Kennung. Dort ersetzt sie den frueheren
 * „← <kennung>"-Knopf, der dieselbe Vorgaengerin oeffnete — deshalb steht sie dort fuer
 * JEDE Vorgaengerin, auch ohne `kette_an` (dann nur „vorher: <Titel>").
 */
/* SELBSTERKLAEREND (Rafael, 03.10.2026 17:16): „↰ vorher: …" las sich fuer Aussenstehende wie
   ein Zeitstempel. In der Kopfzeile heisst sie jetzt „Vorige Bühne: <Titel>". Die lange Zeile
   (`kettenZeileText`, ohne `inZeile`) bleibt unveraendert. */
export function kettenKurzText(vorher: Pick<BuehneAnsicht, 'titel' | 'entscheidungen'>, mitAntwort: boolean): string {
  const e = mitAntwort ? letzteAntwort(vorher) : null;
  return e ? `Vorige Bühne: ${vorher.titel} · deine Antwort: ${e.titel} → ${antwortWort(e)}` : `Vorige Bühne: ${vorher.titel}`;
}

export function BuehnenKettenZeile({ buehne, onOeffnen, inZeile }: {
  buehne: BuehneAnsicht;
  /** Oeffnet eine Buehne per Kennung — derselbe Weg wie die Vor/Zurueck-Knoepfe. */
  onOeffnen: (id: string) => void;
  /** Als Brotkrume in einer Knopfzeile (siehe `kettenKurzText`). */
  inZeile?: boolean;
}) {
  const mitAntwort = zeigtKettenZeile(buehne);
  const ids = mitAntwort || inZeile ? buehne.vorgaenger ?? [] : [];
  const schluessel = ids.join('|');
  const [geladen, setGeladen] = useState<Geladen[]>([]);
  /* Zaehlt die Wiederanlaeufe nach einem Fehlschlag (31.10.2026, b-homepage-ffg: ein Timeout der Kette unter
   * Serverlast blieb fuer immer als „nicht ladbar“ stehen, weil nur ein neuer Schluessel neu lud). */
  const [versuch, setVersuch] = useState({ schluessel, n: 0 });
  // Ein neuer Schluessel beginnt bei 0, ohne zweiten Abruf (Zweitpruefung R1 H2).
  const n = versuch.schluessel === schluessel ? versuch.n : 0;

  useEffect(() => {
    if (!ids.length) { setGeladen([]); return; }
    const controller = new AbortController();
    let wieder: ReturnType<typeof setTimeout> | undefined;
    Promise.all(ids.map(async (id): Promise<Geladen> => {
      try {
        const data = await httpBoundary({ name: 'cui.buehne.kette', url: buehnenLeseUrl(id), schema: KettenBuehne,
          timeoutMs: 10000,
          onStatus: status => status >= 400 ? { throw: new Error(buehnenLeseFehlermeldung(id, status)) } : undefined });
        return { id, buehne: data as unknown as BuehneAnsicht, fehler: null };
      } catch (err) {
        return { id, buehne: null, fehler: err instanceof Error ? err.message : String(err) };
      }
    })).then(liste => {
      if (controller.signal.aborted) return;
      /* Was schon einmal geladen war, bleibt stehen; nur Fehlendes wird als Fehler gezeigt (fail loud). */
      setGeladen(alt => liste.map(g => g.buehne ? g : (alt.find(a => a.id === g.id && a.buehne) ?? g)));
      // Fehlgeschlagene Vorgaengerinnen gestaffelt erneut lesen: 3, 6, 12, 24, dann alle 60 s.
      if (liste.some(g => !g.buehne)) wieder = setTimeout(() => setVersuch({ schluessel, n: n + 1 }), Math.min(60000, 3000 * 2 ** n));
    });
    return () => { controller.abort(); clearTimeout(wieder); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel, n]);

  if (!ids.length) return null;
  if (inZeile) {
    return <>
      {ids.map(id => {
        const g = geladen.find(x => x.id === id);
        /* Fail loud, aber ohne Kennung im Text: die steht im Tooltip. */
        const voll = !g ? 'Vorige Bühne: lädt …'
          : g.buehne ? kettenKurzText(g.buehne, true)
          : `Vorige Bühne: nicht ladbar (${g.fehler})`;
        const text = g?.buehne ? kettenKurzText(g.buehne, mitAntwort) : voll;
        return (
          <button key={id} type="button" data-ai-id="guide-buehne-kette-vorher" data-vorgaenger={id}
            onClick={() => onOeffnen(id)}
            title={`${voll}\nKlick öffnet die vorige Bühne, aus der diese entstanden ist (${id})`}
            aria-label={`Vorige Bühne öffnen: ${g?.buehne?.titel ?? id}`}
            className="guide-buehne-krume"
            style={{ color: g && !g.buehne ? 'var(--tn-orange)' : 'var(--tn-text)' }}>
            ↰ {text}
          </button>
        );
      })}
    </>;
  }
  return (
    <div data-ai-id="guide-buehne-kette" style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 6 }}>
      {ids.map(id => {
        const g = geladen.find(x => x.id === id);
        /* Fail loud: eine nicht ladbare Vorgaengerin steht als solche da, nicht als leere Zeile. */
        const text = !g ? `vorher: ${id} · lädt …`
          : g.buehne ? kettenZeileText(g.buehne)
          : `vorher: ${id} · nicht ladbar (${g.fehler})`;
        return (
          <button key={id} type="button" data-ai-id="guide-buehne-kette-vorher" data-vorgaenger={id}
            onClick={() => onOeffnen(id)}
            title={`Vorgängerin ${id} öffnen`}
            style={{ textAlign: 'left', fontSize: 11, padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
              background: 'rgba(122,162,247,.07)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ↶ {text}
          </button>
        );
      })}
    </div>
  );
}
